<template>
  <div
    class="irp-field-grid"
    :class="{ 'irp-field-grid--flat': !hasCard }"
    data-testid="irp-field-grid"
  >
    <p
      v-if="!visibleFields.length"
      class="irp-field-grid__empty"
      data-testid="irp-field-grid-empty"
    >
      {{ emptyMessage }}
    </p>
    <template v-else>
      <div
        v-for="(field, index) in visibleFields"
        :key="`${field.column}_${index}`"
        class="irp-field-grid__field"
      >
        <IRPFieldGridText class="irp-field-grid__label" :text="field.name" />
        <IRPFieldGridText
          class="irp-field-grid__value"
          :text="formatFieldValue(field)"
          :is-masked="true"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import type { RecordFieldType } from '@/types/individual_record';
import { sortByKey } from '@/utils';

import { formatFieldValue, hasFieldValue } from './irp';
import IRPFieldGridText from './IRPFieldGridText.vue';

const {
  fields,
  emptyMessage = 'No details to show',
  hasCard = true,
} = defineProps<{
  fields: RecordFieldType[];
  emptyMessage?: string;
  hasCard?: boolean;
}>();

const visibleFields = computed(() =>
  fields.filter(hasFieldValue).toSorted(sortByKey('name')),
);
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";

.irp-field-grid {
  @apply grid gap-x-24 gap-y-16 rounded-16 border border-neutral-border bg-white py-24 pl-24 pr-16 shadow-component;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
}

.irp-field-grid--flat {
  @apply rounded-none border-0 bg-transparent p-0 shadow-none;
}

.irp-field-grid__empty {
  @apply m-0 text-base text-neutral-text-weak;
  grid-column: 1 / -1;
}

.irp-field-grid__field {
  @apply flex flex-col gap-8 min-w-0;
}

.irp-field-grid__label {
  @apply min-w-0 text-sm font-semibold text-neutral-text-weak;
}

.irp-field-grid__value {
  @apply min-w-0 text-base text-neutral-text;
}
</style>
